import React, { useRef, useState } from 'react';
import { noop } from 'lodash';
import MuiTextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import { InputAdornment } from '@material-ui/core';
import useStyles, { StyledHelperMessage, StyledLabel } from '../TextField/TextField.styles';
import { StyledTextFieldContainer } from './TextField.styles';

export interface TextFieldProps<T = unknown> {
  onChange: (val: string) => void;
  dataQaId: string;
  disabled?: boolean;
  multiline?: boolean;
  value?: string;
  label?: string;
  placeholder?: string;
  errorMsg?: string;
  visibilityToggle?: boolean;
  helperText?: string;
  rows?: string | number;
  onEnter?: () => void;
  onEsc?: () => void;
  onBlur?: () => void;
  inputElRef?: React.RefObject<HTMLInputElement>;
  className?: string;
}

const TextField = <T extends {}>({
  dataQaId,
  disabled,
  multiline = false,
  rows = 1,
  value,
  label,
  placeholder,
  errorMsg,
  onChange,
  onBlur,
  visibilityToggle,
  helperText,
  onEnter = noop,
  onEsc = noop,
  inputElRef,
  className,
}: TextFieldProps<T>): JSX.Element => {
  const ENTER_KEY_CODE = 13;
  const ESC_KEY_CODE = 27;

  const classes = useStyles();

  const inputEl = useRef<HTMLInputElement>(document.createElement('input'));

  const [showInput, setInputVisibility] = useState<boolean>(false);

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement>): void => onChange(e.target.value);

  const blurInput = (): void => {
    if (inputEl && inputEl.current) {
      inputEl.current.blur();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<Element>): void => {
    if (e.keyCode === ENTER_KEY_CODE && !multiline) {
      blurInput();
      onEnter();
    }
    if (e.keyCode === ESC_KEY_CODE && !multiline) {
      blurInput();
      onEsc();
    }
  };

  const handleClickShowPassword = () => setInputVisibility(!showInput);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const visibilityStatus = showInput ? 'HIDE' : 'SHOW';

  const VisibilityButton = () => (
    <div className={classes.endAdornment} data-qa-id={`${dataQaId}-${visibilityStatus.toLowerCase()}-text`}>
      {visibilityStatus}
    </div>
  );

  const VisibilityToggle = () => (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={handleClickShowPassword}
        onMouseDown={handleMouseDownPassword}
        onMouseUp={handleMouseDownPassword}
        edge="end"
        data-qa-id={`${dataQaId}-${visibilityStatus.toLowerCase()}`}
        disabled={disabled}
      >
        <VisibilityButton />
      </IconButton>
    </InputAdornment>
  );

  const HelperMessage = () => (
    <StyledHelperMessage data-qa-id={`${dataQaId}-${errorMsg ? 'error' : 'helper'}`}>
      {errorMsg || helperText}
    </StyledHelperMessage>
  );

  return (
    <StyledTextFieldContainer data-qa-id={`${dataQaId}-container`} data-disabled={disabled} className={className}>
      {label && <StyledLabel data-qa-id={`${dataQaId}-label`}>{label}</StyledLabel>}
      <MuiTextField
        className={multiline ? classes.textarea : classes.input}
        data-qa-id={dataQaId}
        value={value}
        placeholder={placeholder}
        aria-label="text-field"
        variant="outlined"
        inputProps={{ 'data-qa-id': `${dataQaId}-input`, 'aria-label': 'input', value }}
        InputProps={{ endAdornment: visibilityToggle ? <VisibilityToggle /> : null }}
        InputLabelProps={{ shrink: false }}
        type={!showInput && visibilityToggle ? 'password' : 'text'}
        helperText={<HelperMessage />}
        onChange={onValueChange}
        onKeyDown={onKeyDown}
        inputRef={inputElRef || inputEl}
        error={!!errorMsg}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        onBlur={onBlur}
        fullWidth
      />
    </StyledTextFieldContainer>
  );
};

export default TextField;
